import { useCallback, useMemo, useState } from "react";

import { faCircleNotch, faSparkles } from "@fortawesome/pro-regular-svg-icons";
import { faCheck } from "@fortawesome/pro-solid-svg-icons";

import { Button } from "src/design-system/Button";
import { Icon } from "src/design-system/Icon";
import { Pill } from "src/design-system/Pill";
import { Skeleton } from "src/design-system/Skeleton";

const SKELETON_WIDTHS = ["w-80", "w-96", "w-80", "w-64"] as const;

export const SuggestedQuestionsPicker: React.FC<{
  questions: string[];
  isGenerating: boolean;
  onBuildCharts: (questions: string[]) => void;
  onDescribeOwn?: () => void;
  dataLoc?: string;
}> = ({
  questions,
  isGenerating,
  onBuildCharts,
  onDescribeOwn,
  dataLoc = "suggested-questions-picker",
}) => {
  const [selected, setSelected] = useState<string[]>([]);

  const selectedInQuestions = useMemo(
    () => questions.filter((question) => selected.includes(question)),
    [questions, selected],
  );

  const handleToggle = useCallback(
    (question: string) =>
      setSelected((current) =>
        current.includes(question)
          ? current.filter((entry) => entry !== question)
          : [...current, question],
      ),
    [],
  );

  const handleBuildCharts = useCallback(
    () => onBuildCharts(selectedInQuestions),
    [onBuildCharts, selectedInQuestions],
  );

  const isLoading = questions.length === 0;
  if (isLoading && !isGenerating) return null;

  return (
    <div
      className="flex max-w-lg flex-col items-center gap-y-4 px-6 pb-16"
      data-loc={dataLoc}
    >
      <div className="flex items-center gap-x-1.5 text-gray-400">
        <Icon
          icon={isLoading ? faCircleNotch : faSparkles}
          size="xs"
          spin={isLoading}
        />
        <span
          className={
            isLoading
              ? "font-inter-medium-11px"
              : "font-inter-medium-11px uppercase"
          }
          data-loc={`${dataLoc}-label`}
        >
          {isLoading
            ? "Finding the KPIs that matter for this flow…"
            : "Recommended by AI"}
        </span>
      </div>

      <div
        className={
          isLoading
            ? "flex flex-col items-center gap-y-2.5"
            : "flex flex-wrap justify-center gap-x-2 gap-y-2.5"
        }
      >
        {isLoading
          ? SKELETON_WIDTHS.map((width, index) => (
              <Skeleton
                key={`${width}-${index}`}
                dataLoc={`${dataLoc}-skeleton`}
                height="h-6"
                rounded="rounded-full"
                width={width}
              />
            ))
          : questions.map((question) => {
              const isSelected = selectedInQuestions.includes(question);
              return (
                <button
                  key={question}
                  aria-pressed={isSelected}
                  className="cursor-pointer appearance-none rounded-full"
                  data-loc={`${dataLoc}-option`}
                  type="button"
                  onClick={() => handleToggle(question)}
                >
                  <Pill
                    size="md"
                    variant={isSelected ? "indigo" : "outlined-white"}
                  >
                    {isSelected && <Pill.Icon icon={faCheck} />}
                    <Pill.Text>{question}</Pill.Text>
                  </Pill>
                </button>
              );
            })}
      </div>

      <div className="flex items-center gap-x-3 pt-2">
        <Button
          dataLoc={`${dataLoc}-build`}
          iconLeft={faSparkles}
          isDisabled={selectedInQuestions.length === 0}
          variant="primary"
          onClick={handleBuildCharts}
        >
          Build with Copilot
        </Button>
        {onDescribeOwn && (
          <Button
            dataLoc={`${dataLoc}-describe`}
            variant="secondary"
            onClick={onDescribeOwn}
          >
            Describe my own
          </Button>
        )}
      </div>
    </div>
  );
};
